"use client";

import {
  Badge,
  Button,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, EditIcon, FileText } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type SupplierListRow,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopSupplierFormModal } from "@/modules/merchant/presentation/ui/ShopSupplierFormModal";
import {
  ShopSuppliersFilter,
  type ShopSuppliersListSearch,
} from "@/modules/merchant/presentation/ui/ShopSuppliersFilter";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import {
  useSuppliersListQuery,
  useUpdateSupplierMutation,
} from "../api/queries";

const SUPPLIERS_ROUTE = "/app/shops/$shopId/suppliers" as const;
const LIST_ROUTE = "/app/shops/$shopId/purchase-orders" as const;
const PO_FILTER_ROUTE = "/app/shops/$shopId/purchase-orders" as const;

const poListSearch = {
  offset: 0,
  limit: 20,
  status: undefined,
  branchId: undefined,
  supplierId: undefined,
  due: undefined,
  sortBy: undefined,
  sortOrder: undefined,
} as const;

export function ShopSuppliersPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: SUPPLIERS_ROUTE });
  const search = (useSearch({
    from: SUPPLIERS_ROUTE,
  }) ?? {}) as ShopSuppliersListSearch;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canAdjust = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );

  const update = useUpdateSupplierMutation(shopId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState<SupplierListRow | null>(
    null,
  );

  const listQueryParams = useMemo(
    () => ({
      limit,
      offset,
      search: search.search,
      activeFilter: search.activeFilter,
    }),
    [limit, offset, search.search, search.activeFilter],
  );

  const suppliersList = useSuppliersListQuery(shopId, listQueryParams);

  const rows = suppliersList.data?.data ?? [];
  const total = suppliersList.data?.meta.total ?? 0;

  const toggleActive = useCallback(
    async (id: string, isActive: boolean) => {
      try {
        await update.mutateAsync({
          supplierId: id,
          body: { isActive: !isActive },
        });
        toast.success(isActive ? "ປິດໃຊ້ງານແລ້ວ" : "ເປີດໃຊ້ງານແລ້ວ");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ບໍ່ສຳເລັດ");
      }
    },
    [update],
  );

  const columns: TanstackReactTable.ColumnDef<SupplierListRow>[] = useMemo(
    () => [
      {
        id: "name",
        header: "ຊື່",
        size: 220,
        cell: ({ row }) => (
          <span className="min-w-0 font-medium">{row.original.name}</span>
        ),
      },
      {
        id: "phone",
        header: "ໂທ",
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-muted-foreground text-xs">
            {row.original.phone ?? "—"}
          </span>
        ),
      },
      {
        id: "refCode",
        header: "ລະຫັດ",
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-muted-foreground text-xs">
            {row.original.refCode ?? "—"}
          </span>
        ),
      },
      {
        id: "poCount",
        header: () => <span className="block text-right tabular-nums">PO</span>,
        size: 72,
        cell: ({ row }) => (
          <span className="block text-right text-sm tabular-nums">
            {row.original.poCount}
          </span>
        ),
      },
      {
        id: "status",
        header: "ສະຖານະ",
        size: 120,
        cell: ({ row }) => {
          const active = row.original.isActive;
          return (
            <Badge
              variant={active ? "success" : "secondary"}
              className="whitespace-nowrap font-normal"
            >
              {active ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
            </Badge>
          );
        },
      },
      {
        id: "updatedAt",
        header: "ອັບເດດ",
        size: 160,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {formatDateTimeLocal(row.original.updatedAt)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 280,
        cell: ({ row }) => {
          const r = row.original;
          if (!shopId) return null;
          if (canAdjust) {
            return (
              <RowActions
                maxInline={2}
                actions={[
                  {
                    label: "ເບິ່ງ PO",
                    icon: <FileText className="size-4" />,
                    onClick: () =>
                      nav({
                        to: PO_FILTER_ROUTE,
                        params: { shopId },
                        search: {
                          ...poListSearch,
                          supplierId: r.id,
                        },
                      }),
                  },
                  {
                    label: "ແກ້ໄຂ",
                    icon: <EditIcon className="size-4" />,
                    onClick: () => {
                      setEditSupplier(r);
                      setModalOpen(true);
                    },
                  },
                  {
                    label: r.isActive ? "ປິດໃຊ້ງານ" : "ເປີດໃຊ້ງານ",
                    onClick: () => void toggleActive(r.id, r.isActive),
                  },
                ]}
              />
            );
          }
          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                nav({
                  to: PO_FILTER_ROUTE,
                  params: { shopId },
                  search: {
                    ...poListSearch,
                    supplierId: r.id,
                  },
                })
              }
            >
              <FileText className="size-4" />
              ເບິ່ງ PO
            </Button>
          );
        },
      },
    ],
    [shopId, canAdjust, nav, toggleActive],
  );

  if (!shopId) return null;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.suppliers(shopId)} />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-semibold text-foreground text-lg">ຜູ້ສະໜອງ</h1>
            <p className="text-muted-foreground text-sm">
              ຈັດການຜູ້ສະໜອງຕໍ່ຮ້ານ — ໃຊ້ເມື່ອສ້າງ PO ແລະກອງລາຍງານ
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                nav({
                  to: LIST_ROUTE,
                  params: { shopId },
                  search: { ...poListSearch },
                })
              }
            >
              <ArrowLeftIcon className="size-4" aria-hidden />
              ກັບ PO
            </Button>
            {canAdjust ? (
              <Button
                type="button"
                onClick={() => {
                  setEditSupplier(null);
                  setModalOpen(true);
                }}
              >
                ເພີ່ມຜູ້ສະໜອງ
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ShopSuppliersFilter />
          <DataTable<SupplierListRow, unknown>
            noDataMessage="ບໍ່ມີລາຍການທີ່ກົງກັບຕົວກອງ."
            isLoading={suppliersList.isLoading}
            columns={columns}
            data={rows}
            offset={offset}
            limit={limit}
            totalCount={total}
            onPaginationChange={(p) => {
              if (!shopId) return;
              nav({
                to: SUPPLIERS_ROUTE,
                params: { shopId },
                search: {
                  ...search,
                  offset: p.offset,
                  limit: p.limit,
                },
              });
            }}
          />
        </div>

        {canAdjust ? (
          <ShopSupplierFormModal
            open={modalOpen}
            onOpenChange={(open) => {
              setModalOpen(open);
              if (!open) setEditSupplier(null);
            }}
            shopId={shopId}
            mode={editSupplier ? "edit" : "create"}
            supplier={editSupplier ?? undefined}
          />
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
